import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { plural, useTranslation } from '../../i18n';
import { getErrorMessage } from '../../utils/errorHandler';
import { useShareCounterpartiesQuery, useShareableDronesQuery } from './flightShares.queries';
import { useGrantFlightSharesMutation } from './flightShares.mutations';
import { shareOutcomeMessage } from './shareDisplay';
import { classifyShareError, isForbiddenError } from './shareErrors';
import type { FlightSharePermission } from '../../types/api';

interface ShareDroneFlightsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Share every flight for drone 102 with Mvented" — one action, N rows.
 *
 * This is the whole point of Stage 3's first bullet: the historical backlog is
 * 165 flights on one drone, and clearing it must not be 165 right-clicks. It
 * maps straight onto the grant endpoint's `drone_serial` form, which resolves
 * the flight set server-side under the caller's own RLS.
 *
 * The dialog is idempotent by construction, so re-running after a partial
 * failure is safe: the API reports what it skipped rather than failing on the
 * rows that already exist.
 *
 * ### The drone picker lists what the caller can SHARE, not what it can see
 *
 * It used to take the dashboard's global serial list, which is `fleet.drones`
 * under RLS — and that policy has an XER-wide arm, so an XER admin was offered
 * every airframe on the platform, customer-owned ones with no XER flights
 * included (#1498: "it's not my drone"). Picking one could only ever answer
 * `SHARE_NO_FLIGHTS`. The picker now reads `GET /api/flight-shares/drones`:
 * the serials the caller's own org has flight rows on, with its own count and
 * whose aircraft it is — so a customer-owned drone XER flew is offered, and
 * labelled as the customer's, with the number of flights the share will carry.
 */
export const ShareDroneFlightsDialog: React.FC<ShareDroneFlightsDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();

  const [serial, setSerial] = useState<string>('');
  const [granteeId, setGranteeId] = useState<string>('');
  const [permission, setPermission] = useState<FlightSharePermission>('read');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const counterpartiesQuery = useShareCounterpartiesQuery(open);
  const dronesQuery = useShareableDronesQuery(open);
  const grant = useGrantFlightSharesMutation();

  const drones = dronesQuery.data?.drones ?? [];
  const selectedDrone = drones.find((d) => d.serial_number === serial);

  // Reset EVERY field, the drone included.
  //
  // The drone used to survive a close/reopen while everything around it
  // cleared, and that combination is what made it dangerous rather than merely
  // untidy: the form looked fresh, so an admin reopening to share drone 105
  // could pick an organization, submit, and hand that customer drone 102's
  // entire flight history. Silent, and precisely the disclosure this feature
  // exists to control. The dialog is mounted unconditionally by
  // SharedView, so `useState` survives every close — nothing else clears
  // it for us.
  const handleClose = () => {
    grant.reset();
    setSerial('');
    setGranteeId('');
    setFromDate('');
    setToDate('');
    setPermission('read');
    onClose();
  };

  const handleSubmit = async () => {
    if (!serial || !granteeId) return;
    try {
      await grant.mutateAsync({
        grantee_organization_id: granteeId,
        permission,
        drone_serial: serial,
        // Omitted rather than sent empty: an absent `from_date` is what makes
        // the API fall back to the drone's recorded handover date, which is the
        // defensible default. Sending '' would be a 422.
        ...(fromDate ? { from_date: fromDate } : {}),
        ...(toDate ? { to_date: toDate } : {}),
      });
    } catch {
      // Rendered inline below.
    }
  };

  const classification = grant.error ? classifyShareError(grant.error) : null;
  // The refusal this dialog exists to explain. `SHARE_NO_SOLD_DATE` means the
  // drone has no recorded handover date, so the API refuses to guess where
  // XER's own pre-handover flights end and the customer's begin. The resolution
  // is the From-date field right below the message — highlighted rather than
  // merely mentioned, because nothing in the product currently writes
  // `fleet.drones.sold_date`, so "set the sold date" is not yet actionable.
  const needsFromDate = classification?.kind === 'noSoldDate';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="share-drone-flights-dialog"
    >
      <DialogTitle>{t('flightShares.bulkTitle')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('flightShares.bulkIntro')}
        </Typography>

        {/* Only a 403 means "you are not an admin". On a 500, a dropped
            connection, or the endpoint's own SHARE_OWN_ORG_NOT_FOUND, a real
            admin would otherwise be told they lack permissions they hold —
            while the Organization select sat empty, leaving the dialog unusable
            behind a confidently wrong explanation. Mirrors ObjectShareDialog
            and the shared view, which this site was missed out of. */}
        {counterpartiesQuery.isError &&
          (isForbiddenError(counterpartiesQuery.error) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('flightShares.onlyAdmins')}
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="bulk-share-counterparties-error">
              {getErrorMessage(counterpartiesQuery.error)}
            </Alert>
          ))}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* The picker's own failure, inline and distinct from the
              counterparties one: a 403 here is the same admin gate, anything
              else must name itself rather than leave an empty select. */}
          {dronesQuery.isError && !isForbiddenError(dronesQuery.error) && (
            <Alert severity="error" data-testid="bulk-share-drones-error">
              {getErrorMessage(dronesQuery.error)}
            </Alert>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel id="bulk-share-drone-label">{t('flightShares.drone')}</InputLabel>
            <Select
              labelId="bulk-share-drone-label"
              label={t('flightShares.drone')}
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            >
              {/* Option text is the bare serial on purpose: it is what the
                  operator scans for, and what both test tiers select by. */}
              {drones.map((d) => (
                <MenuItem key={d.serial_number} value={d.serial_number}>
                  {d.serial_number}
                </MenuItem>
              ))}
            </Select>
            {/* Whose aircraft, and how many of OUR flights are on it — the two
                facts that make "105 (owned by Talentis)" a deliberate choice
                rather than a surprise. Stated as a count of what is RECORDED,
                never as what will be shared: the grant defaults to the drone's
                sold_date cut (and honours From/To), so the shared set can be
                smaller, and the outcome toast is what reports it. */}
            {selectedDrone ? (
              <FormHelperText data-testid="bulk-share-drone-help">
                {[
                  !selectedDrone.owned_by_caller && selectedDrone.owner_organization_name
                    ? t('flightShares.droneOwnedBy', {
                        org: selectedDrone.owner_organization_name,
                      })
                    : null,
                  plural(t, selectedDrone.flight_count, {
                    one: 'flightShares.droneFlightsOne',
                    other: 'flightShares.droneFlightsOther',
                  }),
                ]
                  .filter(Boolean)
                  .join(' ')}
              </FormHelperText>
            ) : dronesQuery.isSuccess && drones.length === 0 ? (
              <FormHelperText data-testid="bulk-share-no-drones">
                {t('flightShares.noShareableDrones')}
              </FormHelperText>
            ) : null}
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel id="bulk-share-grantee-label">
              {t('flightShares.selectOrganization')}
            </InputLabel>
            <Select
              labelId="bulk-share-grantee-label"
              label={t('flightShares.selectOrganization')}
              value={granteeId}
              onChange={(e) => setGranteeId(e.target.value)}
            >
              {(counterpartiesQuery.data?.organizations ?? []).map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* The From-date refusal message sits directly above the field that
              resolves it, so the fix is one glance away from the complaint. */}
          {needsFromDate && (
            <Alert severity="warning" data-testid="bulk-share-no-sold-date">
              {t('flightShares.errors.noSoldDate', {
                serial: classification?.droneSerial ?? serial,
              })}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              type="date"
              fullWidth
              label={t('flightShares.fromDate')}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={needsFromDate}
              helperText={t('flightShares.fromDateHelp')}
              inputProps={{ 'data-testid': 'bulk-share-from-date' }}
            />
            <TextField
              size="small"
              type="date"
              fullWidth
              label={t('flightShares.toDate')}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'bulk-share-to-date' }}
            />
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel id="bulk-share-permission-label">{t('flightShares.permission')}</InputLabel>
            <Select
              labelId="bulk-share-permission-label"
              label={t('flightShares.permission')}
              value={permission}
              onChange={(e) => setPermission(e.target.value as FlightSharePermission)}
            >
              <MenuItem value="read">{t('flightShares.permissionRead')}</MenuItem>
              <MenuItem value="write">{t('flightShares.permissionWrite')}</MenuItem>
            </Select>
            <FormHelperText>{t('flightShares.permissionHelp')}</FormHelperText>
          </FormControl>
        </Box>

        {grant.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="bulk-share-result">
            {grant.data.granted === 0 && grant.data.skipped_existing > 0
              ? t('flightShares.nothingNewToShare')
              : shareOutcomeMessage(t, grant.data, {
                  sharedOne: 'flightShares.sharedOne',
                  sharedOther: 'flightShares.sharedOther',
                  skippedOne: 'flightShares.skippedOne',
                  skippedOther: 'flightShares.skippedOther',
                })}
          </Alert>
        )}

        {classification && !needsFromDate && (
          <Alert
            severity={classification.kind === 'noFlights' ? 'info' : 'error'}
            sx={{ mt: 2 }}
            data-testid="bulk-share-error"
          >
            {classification.kind === 'noFlights'
              ? t('flightShares.errors.noFlights')
              : classification.kind === 'illegalPairing'
                ? t('flightShares.errors.illegalPairing')
                : getErrorMessage(grant.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.close')}</Button>
        <Button
          variant="contained"
          disabled={!serial || !granteeId || grant.isPending}
          onClick={handleSubmit}
          startIcon={grant.isPending ? <CircularProgress size={16} /> : undefined}
          data-testid="bulk-share-submit-btn"
        >
          {t('flightShares.bulkShare')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDroneFlightsDialog;
